import React from "react";
import { Check, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tiers = [{
  name: "Community Pillar",
  price: "$5,000",
  description: "Support local initiatives and gain visibility among community leaders.",
  features: ["Logo on Annual Report", "Quarterly Newsletter Mention", "Social Media Shoutout (2x/year)", "2 Tickets to Annual Gala", "Website Supporter Listing"],
  highlight: false
}, {
  name: "Prosperity Partner",
  price: "$10,000",
  description: "Drive economic growth with strategic partnership and high-impact branding.",
  features: ["Logo on Annual Report & Website", "Monthly Newsletter Feature", "Social Media Spotlight (4x/year)", "5 Tickets to Annual Gala", "Event Booth Space", "Program Scholarship Sponsor"],
  highlight: false
}, {
  name: "Legacy Leader",
  price: "$25,000+",
  description: "Shape the future of the Hispanic community with premier leadership status.",
  features: ["Keynote Speaking Slot", "Premium Logo Placement Everywhere", "Dedicated Press Release", "VIP Table at Annual Gala (10 Tickets)", "Advisory Board Seat Option", "Exclusive Impact Report Feature", "Named Scholarship Fund"],
  highlight: true
}];
export default function SponsorshipOpportunities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="sponsorship-opportunities" ref={ref} className={cn("py-24 w-full bg-background transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-primary mb-6">
            Sponsorship Opportunities
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Partner with the COHCC Foundation to expand economic opportunity. 
            Choose a sponsorship level that aligns with your organization's values and impact goals.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {tiers.map((tier, i) => <div key={tier.name} data-index={i} className="h-full">
              <Card className={cn("h-full flex flex-col transition-all duration-300 hover:shadow-xl border-border", tier.highlight ? "bg-primary text-primary-foreground border-primary shadow-2xl scale-105 md:-mt-4 z-10 relative" : "bg-card text-card-foreground")}>
                <CardHeader className="pb-8">
                  {tier.highlight && <div className="mb-4 flex justify-center">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider">
                        <Star className="size-3.5 fill-current" /> Most Impactful
                      </span>
                    </div>}
                  <CardTitle className={cn("text-2xl font-serif font-bold mb-2", tier.highlight ? "text-secondary" : "text-primary")}>
                    {tier.name}
                  </CardTitle>
                  <div className="mb-2">
                    <span className={cn("text-4xl font-bold", tier.highlight ? "text-white" : "text-foreground")}>
                      {tier.price}
                    </span>
                    <span className={cn("text-sm ml-2", tier.highlight ? "text-primary-foreground/80" : "text-muted-foreground")}>
                      / year
                    </span>
                  </div>
                  <CardDescription className={cn("text-base", tier.highlight ? "text-primary-foreground/90" : "text-muted-foreground")}>
                    {tier.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <ul className="space-y-4">
                    {tier.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3">
                        <Check className={cn("size-5 shrink-0 mt-0.5", tier.highlight ? "text-secondary" : "text-primary")} />
                        <span className="text-sm font-medium leading-tight">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter className="pt-8 pb-8">
                  <Button asChild className={cn("w-full h-12 text-base font-bold rounded-lg shadow-md transition-transform hover:scale-[1.02]", tier.highlight ? "bg-secondary text-secondary-foreground hover:bg-secondary/90" : "bg-primary text-primary-foreground hover:bg-primary/90")}>
                    <Link to="/GetInvolvedPage">
                      Sponsor Now
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}